import React, { useState } from "react";
import { simulatePolicy } from "../api/policyApi";
import { ACTION_TYPES, RESOURCE_TYPES, DATA_CLASSIFICATIONS } from "../constants/enums";

const PRESETS = [
  {
    label: "💳 Credit Card PAN",
    description: "Support Detokenize PAN with redaction",
    role: "ROLE_SUPPORT",
    action: "DETOKENIZE",
    resourceType: "DATA",
    resourceName: "CREDIT_CARD",
    classification: "RESTRICTED",
    ipAddress: "10.14.0.22",
    sampleValue: "4532-8921-3940-1289"
  },
  {
    label: "📧 Customer Email",
    description: "User Detokenize Email masking",
    role: "USER",
    action: "DETOKENIZE",
    resourceType: "DATA",
    resourceName: "EMAIL",
    classification: "CONFIDENTIAL",
    ipAddress: "192.168.1.45",
    sampleValue: "alex.rivera@fintechcorp.com"
  },
  {
    label: "🆔 SSN / Govt ID",
    description: "Auditor Access to Restricted SSN",
    role: "AUDITOR",
    action: "DETOKENIZE",
    resourceType: "DATA",
    resourceName: "SSN",
    classification: "RESTRICTED",
    ipAddress: "172.16.8.99",
    sampleValue: "987-65-4321"
  },
  {
    label: "📱 Phone Number",
    description: "Manager Detokenize Phone Partial Mask",
    role: "MANAGER",
    action: "DETOKENIZE",
    resourceType: "DATA",
    resourceName: "PHONE",
    classification: "CONFIDENTIAL",
    ipAddress: "10.0.0.15",
    sampleValue: "+1-555-019-2834"
  }
];

export default function PolicySimulatorModal({
  isOpen,
  onClose,
  apiKey,
  tenantId
}) {
  const [role, setRole] = useState("ROLE_SUPPORT");
  const [action, setAction] = useState("DETOKENIZE");
  const [resourceType, setResourceType] = useState("DATA");
  const [resourceName, setResourceName] = useState("CREDIT_CARD");
  const [classification, setClassification] = useState("RESTRICTED");
  const [ipAddress, setIpAddress] = useState("10.14.0.22");
  const [sampleValue, setSampleValue] = useState("4532-8921-3940-1289");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  async function handleSimulate(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const payload = {
        role,
        action,
        resourceType,
        resourceName,
        classification,
        attributes: {
          ip: ipAddress,
          sampleValue: sampleValue || undefined
        }
      };

      const res = await simulatePolicy(apiKey, tenantId, payload);
      setResult(res);
    } catch (err) {
      setError(err.message || "Simulation failed");
    } finally {
      setLoading(false);
    }
  }

  const applyPreset = (p) => {
    setRole(p.role);
    setAction(p.action);
    setResourceType(p.resourceType);
    setResourceName(p.resourceName);
    setClassification(p.classification);
    setIpAddress(p.ipAddress);
    setSampleValue(p.sampleValue);
    setResult(null);
    setError("");
  };

  return (
    <div className="policy-modal-overlay" onClick={onClose}>
      <div
        className="policy-modal-card policy-simulator-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "780px" }}
      >
        <div className="policy-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "1.3rem" }}>🛡️</span>
            <div>
              <h2 style={{ margin: 0, fontSize: "1.2rem" }}>ABAC / RBAC Policy Decision Simulator</h2>
              <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                Zero-Knowledge Enclave PDP Rule & Transformation Tester
              </span>
            </div>
          </div>
          <button className="policy-modal-close" onClick={onClose} aria-label="Close modal">
            ✕
          </button>
        </div>

        {/* Quick Presets Bar */}
        <div className="policy-presets-bar">
          <span className="preset-title">⚡ Quick Presets:</span>
          <div className="preset-buttons">
            {PRESETS.map((p, idx) => (
              <button
                key={idx}
                type="button"
                className="preset-btn"
                onClick={() => applyPreset(p)}
                title={p.description}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSimulate} className="policy-form">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div className="form-group">
              <label>Subject Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="ROLE_SUPPORT">ROLE_SUPPORT (Tier-2 Ops)</option>
                <option value="USER">USER (Standard Consumer)</option>
                <option value="MANAGER">MANAGER (Elevated Read)</option>
                <option value="ADMIN">ADMIN (Full Governance)</option>
                <option value="AUDITOR">AUDITOR (Read-only Compliance)</option>
                <option value="SERVICE_ACCOUNT">SERVICE_ACCOUNT (M2M)</option>
                <option value="ANONYMOUS">ANONYMOUS (Public Ingress)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Action</label>
              <select value={action} onChange={(e) => setAction(e.target.value)}>
                {ACTION_TYPES.map((act) => (
                  <option key={act} value={act}>
                    {act}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Resource Type</label>
              <select value={resourceType} onChange={(e) => setResourceType(e.target.value)}>
                {RESOURCE_TYPES.map((rt) => (
                  <option key={rt} value={rt}>
                    {rt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Resource / PII Type</label>
              <input
                type="text"
                value={resourceName}
                onChange={(e) => setResourceName(e.target.value)}
                placeholder="e.g. CREDIT_CARD, EMAIL, PHONE, SSN"
              />
            </div>

            <div className="form-group">
              <label>Data Classification</label>
              <select value={classification} onChange={(e) => setClassification(e.target.value)}>
                {DATA_CLASSIFICATIONS.map((dc) => (
                  <option key={dc} value={dc}>
                    {dc}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Client IP Address</label>
              <input
                type="text"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
                placeholder="10.14.0.22"
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: "0.75rem" }}>
            <label>Sample Input Data to Transform</label>
            <input
              type="text"
              value={sampleValue}
              onChange={(e) => setSampleValue(e.target.value)}
              placeholder="e.g. 4532-8921-3940-1289 or alex.rivera@fintechcorp.com"
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.25rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Evaluating Policy Enclave..." : "⚡ Run Simulation"}
            </button>
          </div>
        </form>

        {error && (
          <div className="policy-error" style={{ marginTop: "1rem" }}>
            {error}
          </div>
        )}

        {/* Side-by-Side Split Visualization */}
        {result && (
          <div className={`policy-simulation-result-box decision-${result.decision ? result.decision.toLowerCase() : "allow"}`}>
            <div className="sim-result-header">
              <div className="decision-indicator">
                <span className="pdp-badge">PDP Evaluation</span>
                <span className={`status-badge ${result.decision === "ALLOW" ? "active" : "suspended"}`}>
                  {result.decision}
                </span>
                {result.effect && (
                  <span className="status-badge badge-effect">
                    Effect: {result.effect}
                  </span>
                )}
              </div>
              <div className="decision-latency">
                <span>⏱️ Zero-Knowledge PDP Evaluated</span>
              </div>
            </div>

            {/* Split Panels: Plaintext Ingress vs Transformed View */}
            <div className="sim-split-view">
              <div className="sim-split-panel split-ingress">
                <div className="panel-header">
                  <span className="panel-label">Plaintext Ingress Value</span>
                  <span className="panel-tag tag-ingress">INGRESS VALUE</span>
                </div>
                <div className="panel-content">
                  <code>{sampleValue || "(empty input)"}</code>
                </div>
                <div className="panel-meta">
                  <span>Subject: <strong>{role}</strong></span>
                  <span>Action: <strong>{action}</strong></span>
                </div>
              </div>

              <div className="sim-split-arrow">
                <div className="arrow-line"></div>
                <div className="arrow-badge">
                  {result.effect || (result.decision === "ALLOW" ? "TRANSFORM" : "DENIED")}
                </div>
                <div className="arrow-line"></div>
              </div>

              <div className="sim-split-panel split-egress">
                <div className="panel-header">
                  <span className="panel-label">PDP Enclave Transformed View</span>
                  <span className={`panel-tag ${result.decision === "ALLOW" ? "tag-allowed" : "tag-denied"}`}>
                    {result.decision === "ALLOW" ? "TRANSFORMED" : "ACCESS DENIED"}
                  </span>
                </div>
                <div className="panel-content">
                  <code className={result.decision === "ALLOW" ? "code-allowed" : "code-denied"}>
                    {result.transformedSample || (result.decision === "DENY" ? "[ACCESS_DENIED_BY_PDP_POLICY]" : "(no output)")}
                  </code>
                </div>
                <div className="panel-meta">
                  <span>Resource: <strong>{resourceName}</strong></span>
                  <span>Class: <strong>{classification}</strong></span>
                </div>
              </div>
            </div>

            <div className="sim-details-footer">
              <div className="detail-item">
                <span className="detail-label">Matched Policy:</span>
                <span className="detail-val">
                  {result.policyId ? `${result.policyId} (v${result.version || 1})` : "Default Tenant Security Baseline"}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">PDP Decision Reason:</span>
                <span className="detail-val">{result.reason || "Policy rules matched successfully."}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
